import { useEffect, useRef, useState } from "react";
import RightTimeApi from "@/API";
import "@style/Admin.scss";
import { toast } from "react-toastify";
import { prefetchWhenIdle } from "@/utils/runWhenIdle";
import TestRolePermissionsTable from "../elements/tables/TestRolePermissionsTable";
import RecentsColumns from "../elements/tables/RecentsColumns";
import CompanyRolesTable from "../elements/tables/CompanyRolesTable";
import { MdCheckBoxOutlineBlank, MdCheckBox, MdCameraswitch } from "react-icons/md";
import { CgArrowsExchangeV } from "react-icons/cg";
import Enums from "@/enums";
import { apiMessage } from "@/utils/apiMessage";

export default function CompanyRolesTab(props) {
  const { company, updateSpinner, activeTab, adminPerms } = props.tabData;

  const [data, updateData] = useState<any>({ roles: [] });
  const [selectedRole, updateSelectedRole] = useState<any>(null);
  const [contextLoaded, setContextLoaded] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const validationRequestRef = useRef(0);
  const contextRequestRef = useRef(0);

  useEffect(() => {
    if (isOnThisTab()) loadInitialAsync();
    // eslint-disable-next-line
  }, [activeTab]);

  if (!isOnThisTab()) return null;

  function isOnThisTab() {
    return props && activeTab && activeTab === "roles";
  }

  async function prefetchContextAsync() {
    if (contextLoaded || !company?.id) return;
    const requestId = ++contextRequestRef.current;
    try {
      const resp = await RightTimeApi.getAsync(`/CompanyRolesTab/${company.id}/context`);
      if (requestId !== contextRequestRef.current || !resp.ok) return;
      updateData((prev: any) => ({
        ...prev,
        ...resp.jsonData,
        roles: prev.roles,
      }));
      setContextLoaded(true);
    } catch (error) {
      console.error("Error prefetching roles context:", error);
    }
  }

  async function loadValidationAsync() {
    if (!company?.id) return;
    const requestId = ++validationRequestRef.current;
    try {
      const resp = await RightTimeApi.getAsync(`/CompanyRolesTab/${company.id}/validation`);
      if (requestId !== validationRequestRef.current || !resp.ok) return;
      const warningsByRoleId = resp.jsonData?.warningsByRoleId ?? {};
      updateData((prev: any) => ({
        ...prev,
        roles: (prev.roles ?? []).map((role: any) => ({
          ...role,
          warnings: warningsByRoleId[role.id] ?? [],
        })),
      }));
      updateSelectedRole((current: any) => {
        if (!current?.id) return current;
        const warnings = warningsByRoleId[current.id];
        if (!warnings) return current;
        return { ...current, warnings };
      });
    } catch (error) {
      console.error("Error fetching role validation:", error);
    }
  }

  async function loadInitialAsync() {
    if (!company?.id) return;
    updateSpinner(true);
    const resp = await RightTimeApi.getAsync(`/CompanyRolesTab/${company.id}/list`);
    updateSpinner(false);
    if (resp.ok && resp.jsonData) {
      updateData({ roles: resp.jsonData.roles ?? [] });
      updateSelectedRole(null);
      setContextLoaded(false);
      loadValidationAsync();
      prefetchWhenIdle(() => prefetchContextAsync());
    }
  }

  async function getCompanyRolesAsync() {
    if (company.id) {
      updateSpinner(true);
      const selectedId = selectedRole?.id ?? null;
      const resp = await RightTimeApi.getAsync("/CompanyRolesTab/" + company.id);
      updateSpinner(false);
      if (resp.ok && resp.jsonData) {
        updateData(resp.jsonData);
        setContextLoaded(true);

        if (selectedId) {
          const updatedRole = resp.jsonData.roles.find((r: any) => r.id === selectedId);
          updateSelectedRole(updatedRole ?? null);
        } else {
          updateSelectedRole(null);
        }
      }
    }
  }

  async function selectRoleAsync(role: any) {
    if (!role?.id) {
      updateSelectedRole(role);
      return;
    }
    updateSelectedRole(role);
    if (contextLoaded) return;

    setDetailLoading(true);
    try {
      await prefetchContextAsync();
    } finally {
      setDetailLoading(false);
    }
  }

  function isSystemAdmin() {
    return adminPerms?.includes(Enums.ROLE_PERMS.SystemAdmin) ||
      company?.permissions?.some(
        (p) => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin
      );
  }

  function isAdminLike() {
    return adminPerms?.includes(Enums.ROLE_PERMS.SystemAdmin)
      || adminPerms?.includes(Enums.ROLE_PERMS.CompanyAdmin);
  }

  function canEditRoles() {
    return adminPerms?.includes(Enums.ROLE_PERMS.EditRoles)
      || adminPerms?.includes(Enums.ROLE_PERMS.CompanyAdmin)
      || adminPerms?.includes(Enums.ROLE_PERMS.SystemAdmin);
  }
  async function toggleRoleJobAsync(e) {
    let jobId = parseInt(e.currentTarget.getAttribute("data-jobid"));
    let roleId = parseInt(e.currentTarget.getAttribute("data-roleid"));
    updateSpinner(true);

    const resp = await RightTimeApi.putAsync(`/CompanyRole/${roleId}/Job/${jobId}/Toggle`);
    updateSpinner(false);

    if (resp && resp.ok) {
      await getCompanyRolesAsync();
      toast.success(
        "Role '" +
        resp.jsonData.roleName +
        "' " +
        (resp.jsonData.active ? "enabled" : "disabled") +
        " for " +
        resp.jsonData.jobName
      );
    } else {
      const errorMessage = apiMessage(resp);

      if (typeof errorMessage === "string" && errorMessage.trim()) {
        toast.error(errorMessage.trim());
      }
    }
  }

  function isRoleSiteChecked(siteId) {
    if (!selectedRole?.id) return false;
    if (Array.isArray(selectedRole.sites) && selectedRole.sites.includes(siteId)) {
      return true;
    }
    return (data?.roleSites || []).some(
      (rs) => rs.key === selectedRole.id && rs.value === siteId
    );
  }

  async function toggleRoleSiteAsync(e) {
    const siteId = parseInt(e.currentTarget.getAttribute("data-siteid"));
    const roleId = parseInt(e.currentTarget.getAttribute("data-roleid"));
    updateSpinner(true);

    const resp = await RightTimeApi.putAsync(`/CompanyRole/${roleId}/Site/${siteId}/Toggle`);
    updateSpinner(false);

    if (resp && resp.ok) {
      await getCompanyRolesAsync();
      const site = (data.sites || []).find((s) => s.key === siteId);
      toast.success(`Site '${site?.value || siteId}' updated for role`);
    } else {
      toast.error("Failed to update role site link.");
    }
  }

  async function toggleAllSitesForRoleAsync(e) {
    e.stopPropagation();
    if (!selectedRole?.id || !Array.isArray(data?.sites) || data.sites.length === 0) return;
    updateSpinner(true);
    const resp = await RightTimeApi.putAsync("/SitesToggleRole", {
      roleId: selectedRole.id,
      companyId: company.id,
    });
    updateSpinner(false);
    if (resp && resp.ok) {
      await getCompanyRolesAsync();
      const role = data.roles.find((r) => r.id === selectedRole.id);
      toast.success(`All sites toggled for ${role?.name || "role"}`);
    } else {
      toast.error("Failed to toggle all sites for role.");
    }
  }

  async function toggleUserRoleAsync(e) {
    let uId = parseInt(e.currentTarget.getAttribute("data-userid"));
    let rId = parseInt(e.currentTarget.getAttribute("data-roleid"));
    let callData = {
      roleId: rId,
      userId: uId,
      companyId: company.id,
    };
    updateSpinner(true);

    const resp = await RightTimeApi.putAsync(
      "/CompanyUserRoleToggle",
      callData
    );
    updateSpinner(false);

    if (resp && resp.ok) {
      await getCompanyRolesAsync();
      toast.success(
        "Role '" +
        resp.jsonData.roleName +
        "' " +
        (resp.jsonData.active ? "enabled" : "disabled") +
        " for " +
        resp.jsonData.userFirstName +
        " " +
        resp.jsonData.userLastName
      );
    } else {
      const errorMessage = apiMessage(resp);

      if (typeof errorMessage === "string" && errorMessage.trim()) {
        toast.error(errorMessage.trim());
      }
    }
  }

  async function toggleAllJobsForRoleAsync(e) {
    e.stopPropagation();
    const roleJobs = getRoleJobsForTable();
    if (!selectedRole?.id || roleJobs.length === 0) return;
    const jobIds = roleJobs.map((job) => job.id);
    updateSpinner(true);
    const resp = await RightTimeApi.putAsync("/JobsToggleRole", {
      roleId: selectedRole.id,
      jobIds: jobIds,
      companyId: company.id
    });
    updateSpinner(false);
    if (resp && resp.ok) {
      await getCompanyRolesAsync();
      const role = data.roles.find(r => r.id === selectedRole.id);
      toast.success(`All jobs toggled for ${role?.name || 'role'}`);
    }
  }

  function getRoleJobsForTable() {
    return (Array.isArray(data.jobs) ? data.jobs : [])
      .filter((job: any) => job.active !== false)
      .map((job: any) => ({
        id: job.id,
        name: job.name,
        departmentId: job.departmentId ?? null,
        departmentName: job.departmentName ?? null,
      }));
  }

  function isRoleJobChecked(jobId: number) {
    return Array.isArray(selectedRole?.jobs) && selectedRole.jobs.includes(jobId);
  }

  const showDepartmentColumn = !!company?.departmentsEnabled;

  function renderRoleJobRow(job: { id: number; name: string; departmentId: number | null; departmentName: string | null; departmentIds?: number[] | null }, rowKeyPrefix: string, isChecked: boolean) {
    return (
      <tr key={`${rowKeyPrefix}-${job.id}`}>
        <td className="left">{job.name}</td>
        {showDepartmentColumn ? (
          <td className={`left companyJobDepartmentCell${!(job.departmentIds?.length || job.departmentId) ? " missingDepartment" : ""}`}>
            {job.departmentName || "—"}
          </td>
        ) : null}
        <td
          className="actions"
          data-roleid={selectedRole.id}
          data-jobid={job.id}
          onClick={(e) => {
            if (canEditRoles()) toggleRoleJobAsync(e);
          }}
        >
          {isChecked ? (
            <MdCheckBox className={`checkbox checked actionIcon${canEditRoles() ? "" : " disabled"}`} />
          ) : (
            <MdCheckBoxOutlineBlank className={`checkbox unchecked actionIcon${canEditRoles() ? "" : " disabled"}`} />
          )}
        </td>
      </tr>
    );
  }

  async function toggleAllUsersForRoleAsync(e) {
    e.stopPropagation();
    if (!selectedRole?.id || !Array.isArray(data?.users) || data.users.length === 0) return;

    const selectedUserIds = new Set(
      (data.userRoles || [])
        .filter((ur) => ur.value === selectedRole.id)
        .map((ur) => ur.key)
    );
    const totalUsers = data.users.length;
    const checkedCount = data.users.filter((user) => selectedUserIds.has(user.key)).length;
    const shouldEnableAll = checkedCount <= totalUsers / 2;

    const usersToToggle = data.users.filter((user) =>
      shouldEnableAll ? !selectedUserIds.has(user.key) : selectedUserIds.has(user.key)
    );

    if (usersToToggle.length === 0) return;

    updateSpinner(true);
    const results = await Promise.allSettled(
      usersToToggle.map((user) =>
        RightTimeApi.putAsync("/CompanyUserRoleToggle", {
          roleId: selectedRole.id,
          userId: user.key,
          companyId: company.id,
        })
      )
    );
    updateSpinner(false);

    const failures = results.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && r.value && !r.value.ok));
    if (failures.length > 0) {
      const failureMsg = failures.length === 1 
        ? "Failed to toggle role for 1 user" 
        : `Failed to toggle role for ${failures.length} users`;
      toast.error(failureMsg);
    }

    await getCompanyRolesAsync();
  }

  async function togglePermAsync(id) {
    let data = {
      companyId: company.id,
      permissionId: id,
      companyRoleId: selectedRole.id,
    };
    updateSpinner(true);
    const resp = await RightTimeApi.putAsync(
      "/CompanyRolePermissionToggle",
      data
    );
    updateSpinner(false);

    if (resp && resp.ok) {
      const msg =
        "Permission " +
        resp.jsonData.permissionName +
        (resp.jsonData.enabled ? " enabled" : " disabled");
      toast.success(msg);
      await getCompanyRolesAsync();
    } else {
      const errorMessage = apiMessage(resp);

      if (typeof errorMessage === "string" && errorMessage.trim()) {
        toast.error(errorMessage.trim());
      }
    }
  }

  if (data && data.roles) {
    return (
      <div className="companyRolesTab">
        <div className="horizontal">
          <CompanyRolesTable
            company={company}
            roles={data.roles}
            updateData={getCompanyRolesAsync}
            activeTab={activeTab}
            selectedRole={selectedRole}
            updateSelectedRole={selectRoleAsync}
            instructions={data.instructionsContents}
            adminPerms={adminPerms}
          />
        </div>
        {detailLoading && (
          <div className="cttDetailLoading">Loading role details…</div>
        )}
        {selectedRole &&
          selectedRole.id &&
          !detailLoading &&
          contextLoaded &&
          data.roles.find((r) => r.id === selectedRole.id) ? (
          <TestRolePermissionsTable
            role={selectedRole}
            data={data}
            updateData={getCompanyRolesAsync}
            adminPerms={adminPerms}
          />
        ) : null}

        {selectedRole && selectedRole.id && !detailLoading && contextLoaded ? (
          <div className="horizontal tableWrapper factTable roleUsersTable scrollable">
            <table className="formFields">
              <caption>
                <span className="title">Role / Users</span>
              </caption>
              <thead>
                <tr>
                  <th style={{ minWidth: "200px" }}>User</th>
                  <th
                    style={{ minWidth: "150px" }}
                    className="toggleColumnHeader"
                    data-help-id="toggleActive"
                    onClick={(e) => {
                      if (canEditRoles()) toggleAllUsersForRoleAsync(e);
                    }}
                    title={canEditRoles() ? "Toggle all values" : undefined}
                  >
                    <span className="toggleHeaderLabel">
                      {data.roles.find((r) => r.id === selectedRole.id)?.name ?? ""}
                    </span>
                    <span 
                      className="toggleHeaderIcon" aria-hidden="true" 
                      data-help-id="toggleActive">
                      <MdCameraswitch />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.users && data.userRoles
                  ? data.users.map((user) => {
                    return (
                      <tr key={"userRole" + user.key}>
                        <td className="left">{user.value}</td>
                        <td
                          className="actions"
                          data-roleid={selectedRole.id}
                          data-userid={user.key}
                          onClick={(e) => {
                            if (canEditRoles()) toggleUserRoleAsync(e);
                          }}
                        >
                          {data.userRoles.find(
                            (ur) =>
                              ur.key === user.key &&
                              ur.value === selectedRole.id
                          ) ? (
                            <MdCheckBox className={`checkbox checked actionIcon${canEditRoles() ? "" : " disabled"}`} />
                          ) : (
                            <MdCheckBoxOutlineBlank className={`checkbox unchecked actionIcon${canEditRoles() ? "" : " disabled"}`} />
                          )}
                        </td>
                      </tr>
                    );
                  })
                  : null}
              </tbody>
            </table>
          </div>
        ) : null}

        {selectedRole && selectedRole.id && !detailLoading && contextLoaded && (
          <div className="horizontal tableWrapper factTable scrollable RoleJobsTable">
            <table className="formFields">
              <caption>
                <span className="title">Role / Jobs</span>
                <CgArrowsExchangeV
                  className={`toggleButton floatRight${canEditRoles() ? "" : " disabled"}`}
                  onClick={(e) => {
                    if (canEditRoles()) toggleAllJobsForRoleAsync(e);
                  }}
                  title="Toggle all values"
                  style={{ verticalAlign: "middle", cursor: canEditRoles() ? "pointer" : "default" }}
                />
              </caption>
              <thead>
                <tr>
                  <th style={{ minWidth: "200px" }}>Job</th>
                  {showDepartmentColumn ? <th style={{ minWidth: "120px" }}>Department</th> : null}
                  <th
                    style={{ minWidth: "150px" }}
                    className="toggleColumnHeader"
                    data-help-id="toggleActive"
                    onClick={(e) => {
                      if (canEditRoles()) toggleAllJobsForRoleAsync(e);
                    }}
                    title={canEditRoles() ? "Toggle all values" : undefined}
                  >
                    <span className="toggleHeaderLabel">
                      {data.roles.find((r) => r.id === selectedRole.id)?.name ?? ""}
                    </span>
                    <span className="toggleHeaderIcon" aria-hidden="true">
                      <MdCameraswitch />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {getRoleJobsForTable()
                  .filter((job) => isRoleJobChecked(job.id))
                  .map((job) => renderRoleJobRow(job, "job-top", true))}
                {getRoleJobsForTable()
                  .filter((job) => !isRoleJobChecked(job.id))
                  .map((job) => renderRoleJobRow(job, "job-rest", false))}
              </tbody>
            </table>
          </div>
        )}

        {selectedRole && selectedRole.id && !detailLoading && contextLoaded && (
          <div className="horizontal tableWrapper factTable scrollable RoleSitesTable">
            <table className="formFields">
              <caption>
                <span className="title">Role / Sites</span>
                <CgArrowsExchangeV
                  className={`toggleButton floatRight${canEditRoles() ? "" : " disabled"}`}
                  onClick={(e) => {
                    if (canEditRoles()) toggleAllSitesForRoleAsync(e);
                  }}
                  title="Toggle all values"
                  style={{ verticalAlign: "middle", cursor: canEditRoles() ? "pointer" : "default" }}
                />
              </caption>
              <thead>
                <tr>
                  <th style={{ minWidth: "200px" }}>Site</th>
                  <th
                    style={{ minWidth: "150px" }}
                    className="toggleColumnHeader"
                    onClick={(e) => {
                      if (canEditRoles()) toggleAllSitesForRoleAsync(e);
                    }}
                    title={canEditRoles() ? "Toggle all values" : undefined}
                  >
                    <span className="toggleHeaderLabel">
                      {data.roles.find((r) => r.id === selectedRole.id)?.name ?? ""}
                    </span>
                    <span className="toggleHeaderIcon" aria-hidden="true">
                      <MdCameraswitch />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.sites &&
                  [...data.sites]
                    .sort((a, b) => {
                      const aChecked = isRoleSiteChecked(a.key);
                      const bChecked = isRoleSiteChecked(b.key);
                      if (aChecked === bChecked) return a.value.localeCompare(b.value);
                      return aChecked ? -1 : 1;
                    })
                    .map((site) => (
                      <tr key={"role-site-" + site.key}>
                        <td className="left">{site.value}</td>
                        <td
                          className="actions"
                          data-roleid={selectedRole.id}
                          data-siteid={site.key}
                          onClick={(e) => {
                            if (canEditRoles()) toggleRoleSiteAsync(e);
                          }}
                        >
                          {isRoleSiteChecked(site.key) ? (
                            <MdCheckBox className={`checkbox checked actionIcon${canEditRoles() ? "" : " disabled"}`} />
                          ) : (
                            <MdCheckBoxOutlineBlank className={`checkbox unchecked actionIcon${canEditRoles() ? "" : " disabled"}`} />
                          )}
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        )}

        {selectedRole && selectedRole.id && !detailLoading && contextLoaded && isAdminLike() && (
          <div className="horizontal tableWrapper factTable scrollable rolePermissionsTable">
            <table>
              <caption>
                <span className="title">Role / Permissions</span>
              </caption>
              <thead>
                <tr>
                  <th style={{ minWidth: "175px" }}>Permission Name</th>
                  <th style={{ minWidth: "150px" }}>
                    {data.roles.find((r) => r.id === selectedRole.id)?.name ??
                      ""}
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* Show permissions that exist in data.rolePermissions first */}
                {data.rolePermissions && data.permissions &&
                  data.rolePermissions
                    .filter(rp => rp.roleId === selectedRole.id)
                    .map((rp, i) => {
                      const perm = data.permissions.find(p => p.key === rp.permissionId);
                      if (!perm) return null;
                      return (
                        <tr key={"perms-top-" + i}>
                          <td className="left">{perm.value}</td>
                          <td
                            onClick={() => {
                              if (canEditRoles()) togglePermAsync(perm.key);
                            }}
                            className="actions"
                          >
                            <MdCheckBox className={`checkbox checked actionIcon${canEditRoles() ? "" : " disabled"}`} />
                          </td>
                        </tr>
                      );
                    })}
                {/* Show the rest of the permissions below */}
                {data.permissions &&
                  data.permissions
                    .filter((perm) => {
                      // Exclude permissions already shown above
                      return !data.rolePermissions.some(
                        (rp) => rp.roleId === selectedRole.id && rp.permissionId === perm.key
                      );
                    })
                    .map((perm, i) => {
                      return (
                        <tr key={"perms-rest-" + i}>
                          <td className="left">{perm.value}</td>
                          <td className="permissionToggle actions"
                            onClick={() => {
                              if (canEditRoles()) togglePermAsync(perm.key);
                            }}
                          >
                            <MdCheckBoxOutlineBlank className={`checkbox unchecked actionIcon${canEditRoles() ? "" : " disabled"}`} />
                          </td>
                        </tr>
                      );
                    })}
              </tbody>
            </table>
          </div>
        )}
        {/* {(() => { debugger; })()} */}
        {selectedRole && selectedRole.id && !detailLoading && contextLoaded && isAdminLike() && (
          <div className="horizontal">
            <RecentsColumns
              selectedRole={selectedRole}
              data={data}
              updateData={getCompanyRolesAsync}
              updateSpinner={updateSpinner}
            />
          </div>)}
      </div>
    );
  } else {
    return null;
  }
}

